<template>
	<div class="dialog-panel">
		<header class="header" v-if="$slots['header-left'] || $slots['title-text']">
			<div class="header--left">
				<slot name="header-left">
					<h1 class="title">
						<slot name="title-text" :disabled="disabled"></slot>
					</h1>
				</slot>
			</div>
			<div class="header--right">
				<CloseButton
					:disabled="disabled"
					size="SMALL"
					type="TERTIARY"
					@click="onClose"
				/>
			</div>
		</header>
		<section class="sub-header" v-if="$slots['sub-header']">
			<slot name="sub-header"></slot>
		</section>
		<main :class="bodyClasses">
			<slot name="body" :disabled="disabled" :onClose="onClose"></slot>
		</main>
		<footer :class="footerClasses" v-if="$slots.footer">
			<slot name="footer" :disabled="disabled"></slot>
		</footer>
	</div>
</template>

<script>
import {
	Enum
} from "@teaming/common-enums";

import CloseButton from "../buttons/CloseButton.vue";

export const FooterType = Enum.create( {
	DEFAULT: "DEFAULT",
	TRANSPARENT: "TRANSPARENT",
} );

/**
 * Scrollable panel container for use with SparseDialog.
 * @code
 * <SparseDialog>
 *     <DialogPanel>
 *         <template #header></template>
 *         <template #body></template>
 *         <template #footer></template>
 *     </DialogPanel>
 * </SparseDialog>
 */
export default {
	name: "DialogPanel",
	emits: [
		"close",
	],
	components: {
		CloseButton,
	},
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		scrollBody: {
			type: Boolean,
			default: true,
		},
		/** @deprecated - prefer using LiteDialogPanel instead of DialogPanel instead */
		footerType: {
			type: String,
			default: FooterType.DEFAULT,
			validator: value => FooterType.includes( value ),
		},
	},
	computed: {
		bodyClasses() {
			return {
				body: true,
				"body--scrollable": this.scrollBody,
			};
		},
		footerClasses() {
			return {
				footer: true,
				"footer--transparent": ( this.footerType === FooterType.TRANSPARENT ),
			};
		},
	},
	methods: {
		onClose() {
			this.$emit( "close" );
		},
	},
};
</script>

<style lang="scss" scoped>
$footerBackgroundColor: $color1-050;
$titleColor: $color1-700;
$descriptionColor: $color1-500;

.dialog-panel {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	position: absolute;
	top: 0;
	bottom: 0;
	width: 100%;
}

.dialog-panel .header,
.dialog-panel .footer {
	padding: 1.5rem;
}

.dialog-panel .header {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: stretch;
	gap: 0.5rem;

	border-bottom: 0.0625rem solid $borderColor--dark;
}

.dialog-panel .header--left {
	flex: 1;

	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
}

.dialog-panel .header--right {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-end;
}

.dialog-panel .title {
	@include font-and-line-for(2);
	color: $titleColor;
	font-weight: 600;
}

.dialog-panel .body {
	flex: 1;

	position: relative;

	padding-top: 0;
	padding-bottom: 0;

	overflow: hidden;
}

.dialog-panel .body--scrollable {
	@include auto-y-scroll();
}

.dialog-panel .footer {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.5rem;

	background-color: $footerBackgroundColor;
	border-top: 0.0625rem solid $borderColor--dark;
}

.dialog-panel .footer--transparent {
	background-color: transparent;
	border-top: none;
}
</style>
